import { useEffect, useState } from "react";
import api, { money } from "../lib/api";
import { Button } from "../components/ui/button";
import { Copy, Check, Users, Gift, Share2, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

export default function Referral() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const symbol = "₹";

  useEffect(() => {
    api.get("/referrals").then((r) => setData(r.data));
  }, []);

  const link = data ? `${window.location.origin}/register?ref=${data.referral_code}` : "";

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Join HARMONEYS", text: "Pay your bills, save money and earn rewards with HARMONEYS — Enjoy The Difference!", url: link }); } catch {}
    } else copy(link);
  };

  if (!data) return null;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Refer & Earn</h1>
        <p className="text-slate-500 mt-1">Invite friends — you get {money(data.referrer_reward, symbol)}, they get {money(data.referee_reward, symbol)} on their first bill.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-8 text-white">
          <Gift className="w-10 h-10" />
          <p className="text-emerald-50 text-sm uppercase tracking-widest font-bold mt-4">Your referral code</p>
          <p className="font-display text-4xl font-extrabold tracking-wide mt-1" data-testid="referral-code">{data.referral_code}</p>

          <div className="mt-6 bg-white/15 rounded-xl p-3 flex items-center gap-2">
            <input readOnly value={link} className="flex-1 bg-transparent text-sm text-white/90 outline-none" data-testid="referral-link" />
            <Button size="sm" onClick={() => copy(link)} data-testid="copy-link-btn" className="rounded-full bg-white text-emerald-700 hover:bg-emerald-50 font-semibold shrink-0">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          <Button onClick={share} data-testid="share-btn" className="mt-4 rounded-full bg-slate-900 hover:bg-slate-800 font-semibold">
            <Share2 className="w-4 h-4 mr-2" /> Share invite
          </Button>
        </div>

        <div className="grid gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <Users className="w-7 h-7 text-slate-700" />
            <p className="text-xs uppercase tracking-widest font-bold text-slate-400 mt-3">Total referrals</p>
            <p className="font-display text-3xl font-extrabold text-slate-900" data-testid="total-referrals">{data.total_referrals}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <CheckCircle2 className="w-7 h-7 text-emerald-500" />
            <p className="text-xs uppercase tracking-widest font-bold text-slate-400 mt-3">Rewarded</p>
            <p className="font-display text-3xl font-extrabold text-emerald-600" data-testid="rewarded-referrals">{data.rewarded_count}</p>
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl font-bold text-slate-900 mb-4">Your referrals</h2>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {data.referrals.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-10 h-10 text-slate-300 mx-auto" />
              <p className="text-slate-500 text-sm mt-3">No referrals yet. Share your link to start earning!</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.referrals.map((r, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-4" data-testid={`referral-item-${i}`}>
                  <div>
                    <p className="font-semibold text-slate-900">{r.name}</p>
                    <p className="text-xs text-slate-400">Joined {new Date(r.joined).toLocaleDateString()}</p>
                  </div>
                  {r.rewarded ? (
                    <span className="flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full"><CheckCircle2 className="w-3.5 h-3.5" /> Rewarded</span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 px-3 py-1 rounded-full"><Clock className="w-3.5 h-3.5" /> Pending first bill</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
